import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { InnerSiteHeaderWrapper } from './siteHeader/InnerSiteHeaderWrapper';
import { SiteHeaderLeftWrapper } from './siteHeader/SiteHeaderLeftWrapper';
import { SiteHeaderLogoLink } from './siteHeader/SiteHeaderLogoLink';
import { SiteHeaderRightWrapper } from './siteHeader/SiteHeaderRightWrapper';
import { SiteHeaderShell } from './siteHeader/SiteHeaderShell';
import { TopNav } from './siteHeader/TopNav';
import { TopNavCollapsibleMenu } from './siteHeader/TopNavCollapsibleMenu';
import { TopNavMenu } from './siteHeader/TopNavMenu';
import { TopNavMenuItem } from './siteHeader/TopNavMenuItem';
import { TopNavMenuLink } from './siteHeader/TopNavMenuLink';
import { TopNavSubmenu, SubmenuMenu } from './siteHeader/TopNavSubmenu';
import { TopNavUserLink } from './siteHeader/TopNavUserLink';
import { TopNavHamburgerButton } from './siteHeader/TopNavHamburgerButton';
import { TopNavHamburgerButtonWrapper } from './siteHeader/TopNavHamburgerButtonWrapper';
import { createBreakpoint } from '../util/createBreakpoint';
import { SiteHeaderContext } from './siteHeader/SiteHeaderContext';

const Wrapper = styled(InnerSiteHeaderWrapper)`
   ${TopNavCollapsibleMenu} {
      display: ${({ mobileMenuOpen }) => (mobileMenuOpen ? 'block' : 'none')};
      position: absolute;
      top: ${({ theme }) => theme.sizes.siteHeaderHeight};
      right: 0;
      padding: 0;
      background-color: ${({ theme }) => theme.brandColors.white};
      box-shadow: -1px 5px 5px 0 hsla(222, 34%, 7%, 0.07);
      height: ${({ theme }) => `calc(100vh - ${theme.sizes.siteHeaderHeight})`};
      min-width: 250px;

      ${SubmenuMenu} {
         position: static;
         box-shadow: none;
      }
   }

   ${TopNavCollapsibleMenu} {
      ${TopNavMenuItem}, ${TopNavMenuLink} {
         display: block;
         width: 100%;
      }
   }

   ${({ theme, expandMenuAt }) =>
      createBreakpoint('above', expandMenuAt, theme)} {
      ${TopNavCollapsibleMenu} {
         display: block;
         position: static;
         background-color: transparent;
         box-shadow: none;
         height: auto;
         padding: 0 1rem;
         overflow: visible;

         ${SubmenuMenu} {
            position: absolute;
            border: 1px solid ${({ theme }) => theme.brandColors.extraLightGray};
            box-shadow: -1px 5px 5px 0 hsla(222, 34%, 7%, 0.07);

            ${TopNavMenuLink} {
               &::after {
                  display: inline-block;
                  top: 3px;
                  border-top: 7px solid transparent;
                  border-bottom: 7px solid transparent;

                  border-left: 6px solid
                     ${({ theme }) => theme.brandColors.mediumLightGray};
               }
            }

            ${SubmenuMenu} {
               padding-left: 0;
               transform: translateX(100%);
               top: 0;
            }
         }
      }

      ${TopNavCollapsibleMenu} {
         ${TopNavMenuItem}, ${TopNavMenuLink} {
            display: inline-block;
            width: auto;
         }
      }

      ${TopNavMenuLink} {
         color: ${({ theme }) => theme.brandColors.white};

         &:hover,
         &:focus,
         &:active {
            outline: none;
            color: ${({ theme }) => theme.semanticColors.secondary};
            border-bottom-color: ${({ theme }) =>
               theme.semanticColors.secondary};
         }

         &::after {
            position: relative;
            top: 13px;
            right: 0;
            border-top-color: ${({ theme }) => theme.brandColors.white};
         }
      }

      ${SubmenuMenu} {
         ${TopNavMenuLink} {
            color: ${({ theme }) => theme.brandColors.darkGray};

            &:hover,
            &:focus,
            &:active {
               color: ${({ theme }) => theme.brandColors.darkGray};
            }
         }
      }

      ${TopNavHamburgerButtonWrapper} {
         display: none;
      }
   }
`;

const SiteHeader = ({ children, expandMenuAt = 'lg', fixed = false }) => {
   const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

   const wrapperRef = useRef();

   const handleBlur = e => {
      if (
         !(
            e.relatedTarget ===
               document.querySelector('[data-rcl-hamburger-button]') ||
            [...document.querySelectorAll('[data-rcl-collapsible-menu]')].find(
               node => node.contains(e.relatedTarget)
            )
         )
      ) {
         setMobileMenuOpen(false);
      }
   };

   useEffect(() => {
      const handleDomClick = ({ target }) => {
         if (
            (mobileMenuOpen && target && target.nodeName === 'A') ||
            (wrapperRef && !wrapperRef.current.contains(target))
         ) {
            setMobileMenuOpen(false);
         }
      };

      document.addEventListener('click', handleDomClick);

      return () => document.removeEventListener('click', handleDomClick);

      // eslint-disable-next-line
   }, []);

   return (
      <SiteHeaderContext.Provider
         value={{ mobileMenuOpen, setMobileMenuOpen, expandMenuAt }}
      >
         <SiteHeaderShell fixed={fixed} onBlur={handleBlur}>
            <Wrapper
               mobileMenuOpen={mobileMenuOpen}
               expandMenuAt={expandMenuAt}
               ref={wrapperRef}
               className="container"
            >
               {children}
            </Wrapper>
         </SiteHeaderShell>
      </SiteHeaderContext.Provider>
   );
};

SiteHeader.propTypes = {
   children: PropTypes.node.isRequired,
   fixed: PropTypes.bool,
   expandMenuAt: PropTypes.string,
};

export {
   SiteHeader,
   InnerSiteHeaderWrapper,
   SiteHeaderLeftWrapper,
   SiteHeaderLogoLink,
   SiteHeaderRightWrapper,
   SiteHeaderShell,
   TopNav,
   TopNavCollapsibleMenu,
   TopNavMenu,
   TopNavMenuItem,
   TopNavMenuLink,
   TopNavSubmenu,
   TopNavUserLink,
   TopNavHamburgerButtonWrapper,
   TopNavHamburgerButton,
   SiteHeaderContext,
};

export { TopNavLogo } from './siteHeader/TopNavLogo';
